import Box from "@mui/material/Box";
import MuiModal from "@mui/material/Modal";
import MuiTypography from "@mui/material/Typography";
import { cleanProps } from "../internal/cleanProps";
import { DIALOG_ROOT_ID } from "../internal/dialog-root";
import { tokens } from "../tokens";
import type { DialogProps } from "../types";

const sp = tokens.spacing;

/**
 * ADR-20261102-h2fd (AC-13): H2's dialog -- a scrim over the page's main region (not the whole
 * window) with a 460px panel held 24px from the top as the page scrolls; heading2 title, 12px
 * between blocks. Material UI's Modal supplies the focus trap, Escape and the inert background;
 * it is mounted into the main region so the scrim covers only that, as in H2.
 */
export function Dialog({ open, title, children, onClose, testId }: Readonly<DialogProps>) {
  const titleId = `${testId ?? "portal-dialog"}-title`;
  return (
    <MuiModal
      open={open}
      onClose={onClose}
      hideBackdrop
      container={() => document.getElementById(DIALOG_ROOT_ID) ?? document.body}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        backgroundColor: tokens.colour.h2.dialogScrim,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: `${sp.md}px`,
      }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        {...cleanProps({ "data-testid": testId })}
        sx={{
          position: "sticky",
          top: `${sp.lg}px`,
          marginTop: `${sp.lg}px`,
          width: "100%",
          maxWidth: 460,
          display: "flex",
          flexDirection: "column",
          gap: `${sp.sm}px`,
          padding: { xs: `${sp.md}px`, sm: `${sp.lg}px` },
          backgroundColor: tokens.colour.neutral.white,
          borderRadius: `${tokens.interactionState.radius.surface}px`,
          border: `${tokens.interactionState.border.hairlineWidth}px solid ${tokens.colour.neutral.borderSubtle}`,
          outline: "none",
        }}
      >
        <MuiTypography variant="h3" component="h2" id={titleId}>
          {title}
        </MuiTypography>
        {children}
      </Box>
    </MuiModal>
  );
}
